import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Ellipsis, SlidersHorizontal, Trash2, type LucideIcon } from 'lucide-react';
import { AnimatedToggleGroup, AnimatedToggleGroupItem } from '../../ui/animated-toggle-group';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { roleForeground } from '../board-grid/roles';
import {
  PHOTO_HOLD_ROLE_ICONS,
  PHOTO_HOLD_ROLE_SWATCHES,
  type PhotoHoldRole,
} from '../photo-hold-roles';
import { HoldHandButton, type HoldHandControl } from './HoldHandButton';
import { HoldStepButton, type HoldStepControl } from './HoldStepButton';
import { PopoverPanel, PopoverRoundButton, PopoverRow } from './popover-parts';

/**
 * The three roles every climb is made of, on the bar itself: where it starts,
 * what it is climbed on, where it ends. Legend order.
 */
const BAR_ROLES: PhotoHoldRole[] = ['start', 'hand', 'finish'];

/**
 * The two that qualify a hold rather than place it in the climb — a foot, a
 * match — behind one button. A wall is mostly hands; the bar has room for the
 * roles a thumb reaches for every few presses, and these two are picked once in
 * a while from the menu that button opens.
 */
const OTHER_ROLES: PhotoHoldRole[] = ['foot', 'match'];

interface HoldBarProps {
  /**
   * What the hold in hand is — or, with nothing in hand, what the next one
   * placed will be. The bar never goes away, so this is always one or the other.
   */
  role: PhotoHoldRole;
  onRole: (role: PhotoHoldRole) => void;
  /**
   * Why a role cannot be given right now, or null when it can. A board problem
   * has room for one pair of starts and one of finishes, and a foot chip is too
   * small to pull on; a photo caps the two ends the same way.
   */
  unavailable?: (role: PhotoHoldRole) => string | null;
  /**
   * Put the hold down, leaving it on the wall. Pressing the role it already
   * wears is what asks for it — see the note on the group below.
   */
  onDismiss: () => void;
  onDelete: () => void;
  /** Whether there is anything in hand for the bin to take. */
  canDelete: boolean;
  deleteLabel: string;
  /**
   * The hand it is taken with, and the swap — where the circuit forces hands.
   * Dead while nothing in hand names a hand; absent on every other climb.
   */
  hand?: HoldHandControl;
  /**
   * Its place in the circuit, where the climb is one — dead while nothing in
   * hand has a place. Absent on an ordinary climb, whose holds are a set.
   */
  step?: HoldStepControl;
  /**
   * The way to the filter of a scanned wall's holds (`HoldFilterPanel`), once
   * there are holds to filter. `active` is a filter hiding something, so the
   * bar says so while the panel is shut.
   */
  filter?: HoldBarFilter;
  testId?: string;
}

export interface HoldBarFilter {
  open: boolean;
  active: boolean;
  onToggle: () => void;
}

/**
 * What can be done to the hold in hand, along the bottom of the picture — and,
 * with nothing in hand, what the next one will be.
 *
 * One row, icons only, so it fits under a thumb on a phone held at the wall:
 * the three roles a climb cannot do without as a toggle group whose coloured
 * pill slides from the old answer to the new one, then the button that opens the
 * other two, the bin, and — on a circuit — the hand that takes the hold where
 * hands are forced, and the hold's place in the run. The roles, their colours
 * and their icons are the board's (see `photo-hold-roles`), so a hold means the
 * same thing wherever it is painted.
 *
 * Always there. It used to arrive with the first hold and stand at it (the gym
 * panel's scans page keeps that — see `HoldActions`), which meant the colour of
 * the next circle could only be said *after* drawing it, and the panel covered
 * the wall the next press was aimed at. Docked, it is the brush as much as the
 * panel: press a role, then press the wall.
 */
export function HoldBar({
  role,
  onRole,
  unavailable,
  onDismiss,
  onDelete,
  canDelete,
  deleteLabel,
  hand,
  step,
  filter,
  testId,
}: HoldBarProps) {
  const { t } = useTranslation();

  return (
    <PopoverPanel testId={testId} className="flex-row items-center gap-1 rounded-full p-1">
      <AnimatedToggleGroup
        fit
        // One of the other two roles lights the button that opens them instead,
        // and the group stands with no pill at all.
        value={BAR_ROLES.includes(role) ? role : ''}
        onValueChange={(next) => onRole(next as PhotoHoldRole)}
        // Pressing the role the hold already wears puts the hold down. It is the
        // one press in the row that would otherwise do nothing, and "this one is
        // right, I am done with it" is exactly what pressing its own colour
        // means. The hold stays on the wall; only the handles go — and the next
        // press on the wall draws in that same colour.
        onEmpty={onDismiss}
        aria-label={t('climbs.holdRoles.legend.title')}
        className="w-auto gap-1 rounded-full border-none bg-transparent p-0"
        highlightClassName="rounded-full shadow-none"
        highlightStyle={{ backgroundColor: PHOTO_HOLD_ROLE_SWATCHES[role] }}
      >
        {BAR_ROLES.map((option) => {
          const why = unavailable?.(option) ?? null;
          const Icon: LucideIcon = PHOTO_HOLD_ROLE_ICONS[option];
          const label = t(`climbs.holdRoles.${option}.label`);

          return (
            <AnimatedToggleGroupItem
              key={option}
              value={option}
              disabled={!!why}
              aria-label={why ?? label}
              title={why ?? label}
              className="flex size-9 items-center justify-center rounded-full p-0"
              // The pill behind the chosen one carries the role's colour, so the
              // icon on it has to be whichever of black or white reads on that.
              style={
                option === role
                  ? { color: roleForeground(PHOTO_HOLD_ROLE_SWATCHES[option]) }
                  : { color: PHOTO_HOLD_ROLE_SWATCHES[option] }
              }
            >
              <Icon className="size-4" aria-hidden />
            </AnimatedToggleGroupItem>
          );
        })}
      </AnimatedToggleGroup>

      <OtherRolesButton role={role} onRole={onRole} unavailable={unavailable} />

      <PopoverRoundButton label={deleteLabel} onClick={onDelete} disabled={!canDelete}>
        <Trash2 className="size-4" aria-hidden />
      </PopoverRoundButton>

      {/* Which hand takes this hold, and the swap — only where the circuit
          forces hands, since only then is there a hand to swap. */}
      {hand && <HoldHandButton control={hand} />}

      {/* Where this hold comes in the circuit, and the two ways to move it —
          only on a circuit, since only a circuit has an order to be in. */}
      {step && <HoldStepButton step={step} />}

      {/* Which of the scan's holds the wall offers — only on a scanned wall,
          since only a scan has holds to leave out. A dot while it hides any. */}
      {filter && (
        <span className="relative">
          <PopoverRoundButton
            label={t('climbEditor.holdFilter.button')}
            onClick={filter.onToggle}
            pressed={filter.open}
            testId="hold-filter-button"
          >
            <SlidersHorizontal className="size-4" aria-hidden />
          </PopoverRoundButton>
          {filter.active && (
            <span
              className="pointer-events-none absolute right-0.5 top-0.5 size-2 rounded-full bg-primary"
              aria-hidden
            />
          )}
        </span>
      )}
    </PopoverPanel>
  );
}

/**
 * The foot and the match, behind one button that opens them above the bar.
 *
 * While the hold in hand is one of the two, the button wears it — its icon on
 * its colour, exactly as the pill in the group does — so the bar always shows
 * what is in hand somewhere along it, with a chevron beside the icon so it still
 * reads as a way in: a filled button wearing one role would otherwise look like
 * the pill next to it, an answer rather than a list. Otherwise it is the plain
 * "more" of a row of icons, which says so by itself, and pressing it lists the
 * two by name.
 */
function OtherRolesButton({
  role,
  onRole,
  unavailable,
}: Pick<HoldBarProps, 'role' | 'onRole' | 'unavailable'>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wearing = OTHER_ROLES.includes(role) ? role : null;
  const Icon: LucideIcon = wearing ? PHOTO_HOLD_ROLE_ICONS[wearing] : Ellipsis;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('climbEditor.holds.otherRoles')}
          title={t('climbEditor.holds.otherRoles')}
          data-testid="hold-other-roles"
          data-role={wearing ?? undefined}
          className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:text-foreground data-[state=open]:text-foreground"
          style={
            wearing
              ? {
                  backgroundColor: PHOTO_HOLD_ROLE_SWATCHES[wearing],
                  borderColor: PHOTO_HOLD_ROLE_SWATCHES[wearing],
                  color: roleForeground(PHOTO_HOLD_ROLE_SWATCHES[wearing]),
                }
              : undefined
          }
        >
          <Icon className="size-4" aria-hidden />
          {wearing && (
            <ChevronDown
              className="-mr-1 size-3"
              aria-hidden
              data-testid="hold-other-roles-chevron"
            />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent side="top" sideOffset={8} className="w-48 rounded-2xl p-1.5">
        {OTHER_ROLES.map((option) => {
          const why = unavailable?.(option) ?? null;
          const OptionIcon: LucideIcon = PHOTO_HOLD_ROLE_ICONS[option];

          return (
            <PopoverRow
              key={option}
              label={t(`climbs.holdRoles.${option}.label`)}
              title={why ?? undefined}
              disabled={!!why}
              active={option === role}
              onClick={() => {
                onRole(option);
                setOpen(false);
              }}
              icon={
                <span
                  className="flex size-7 items-center justify-center rounded-full"
                  style={
                    option === role
                      ? {
                          backgroundColor: PHOTO_HOLD_ROLE_SWATCHES[option],
                          color: roleForeground(PHOTO_HOLD_ROLE_SWATCHES[option]),
                        }
                      : { color: PHOTO_HOLD_ROLE_SWATCHES[option] }
                  }
                >
                  <OptionIcon className="size-4" aria-hidden />
                </span>
              }
            />
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
